<template>
  <div class="license-filter">
    <div class="filter-grid">
      <!-- Optimization type -->
      <div class="filter-item width-2">
        <h4>{{ i18n.t('optimizationType') }}</h4>
        <SmSelect
          v-model:selection="selectedTypeOptions"
          :options="typeOptions"
          :label="i18n.t('selectType')"
          :multiselect="true"
          searchable
          @change="
            (selection) => {
              if (selection) selectType(selection)
            }
          " />
      </div>
      <!-- License -->
      <div class="filter-item width-2">
        <h4>{{ i18n.t('license.assignedLicense', { count: 2 }) }}</h4>
        <SmSelect
          v-model:selection="selectedLicensesOptions"
          :options="licenseOptions"
          :label="i18n.t('selectLicenses')"
          :multiselect="true"
          searchable
          @change="
            (selection) => {
              if (selection) handleLicenseSelection(selection)
            }
          " />
      </div>
      <!-- Suggested license -->
      <div class="filter-item width-2">
        <h4>{{ i18n.t('license.suggestedLicense', { count: 2 }) }}</h4>
        <SmSelect
          v-model:selection="selectedSuggestedLicensesOptions"
          :options="props.suggestedLicenseOptions"
          :label="i18n.t('selectLicenses')"
          :multiselect="true"
          searchable
          @change="
            (selection) => {
              if (selection) handleSuggestedLicenseSelection(selection)
            }
          " />
      </div>
      <div class="filter-item width-2">
        <div class="flex flex-row justify-between">
          <span>{{ i18n.t('showHidden') }}</span>
          <SmSwitch v-model="showHidden" style="font-size: 0.75rem" />
        </div>
      </div>
      <div class="filter-item width-2">
        <div class="flex flex-row justify-between">
          <span>{{ i18n.t('showDeprecated') }}</span>
          <SmSwitch v-model="showDeprecated" style="font-size: 0.75rem" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { Option } from '@/components/sm/SmSelect.vue'
  import { SoftwareLicense, OptimizationType } from '@/client'
  import type { OptimizationFilters } from '@/stores/optimization/optimization.filter'

  const i18n = useI18n()

  const props = defineProps<{
    softwareLicenses: SoftwareLicense[]
    suggestedLicenseOptions: Option<string>[]
  }>()

  const filter = defineModel<OptimizationFilters>('filter', {
    required: true,
  })

  const selectedLicensesOptions = ref<Option<string>[]>([])
  const selectedLicenses = ref<string[]>([])

  const licenseOptions = computed(() => {
    return props.softwareLicenses
      .map((license) => {
        return {
          label: license.name,
          value: license._id,
        }
      })
      .toSorted((a, b) => {
        return a.label.localeCompare(b.label)
      })
  })

  function handleLicenseSelection(selection: Option<string>[]) {
    selectedLicensesOptions.value = selection
    selectedLicenses.value = selection.map((s) => s.value)
  }

  watch(selectedLicenses, () => {
    if (selectedLicenses.value.length === 0) {
      filter.value.licenses = undefined
    } else {
      filter.value.licenses = selectedLicenses.value
    }
  })

  watch(
    () => filter.value.licenses,
    (licenses) => {
      if (!licenses || licenses.length === 0) {
        selectedLicensesOptions.value = []
      } else {
        selectedLicensesOptions.value = licenseOptions.value.filter((license) =>
          licenses.includes(license.value)
        )
      }
    },
    { immediate: true }
  )

  const selectedSuggestedLicensesOptions = ref<Option<string>[]>([])
  const selectedSuggestedLicenses = ref<string[]>([])

  function handleSuggestedLicenseSelection(selection: Option<string>[]) {
    selectedSuggestedLicensesOptions.value = selection
    selectedSuggestedLicenses.value = selection.map((s) => s.value)
  }

  watch(selectedSuggestedLicenses, () => {
    filter.value.suggestedLicenses =
      selectedSuggestedLicenses.value.length === 0
        ? undefined
        : selectedSuggestedLicenses.value
  })

  watch(
    () => filter.value.suggestedLicenses,
    (licenses) => {
      if (!licenses || licenses.length === 0) {
        selectedSuggestedLicensesOptions.value = []
        selectedSuggestedLicenses.value = []
      } else {
        selectedSuggestedLicensesOptions.value =
          props.suggestedLicenseOptions.filter((license) =>
            licenses.includes(license.value)
          )
      }
    },
    { immediate: true }
  )

  const typeOptions = computed(() => {
    return Object.values(OptimizationType)
      .map((type) => {
        return {
          label: i18n.t(`optimizationNames.${type}`),
          value: type,
        }
      })
      .toSorted((a, b) => a.label.localeCompare(b.label))
  })

  // Local state mirrors the model
  const selectedTypeOptions = ref<Option<string>[]>([])

  function selectType(selection: Option<string>[]) {
    selectedTypeOptions.value = selection
    filter.value.type =
      selection.length > 0 ? selection.map((s) => s.value) : undefined
  }

  // keep local state in sync when filter changes externally
  watch(
    () => filter.value.type,
    (types) => {
      if (!types || types.length === 0) {
        selectedTypeOptions.value = []
      } else {
        selectedTypeOptions.value = typeOptions.value.filter((t) =>
          types.includes(t.value)
        )
      }
    },
    { immediate: true }
  )

  const showHidden = ref<boolean>(false)
  const showDeprecated = ref<boolean>(false)

  watch(
    () => filter.value.hidden,
    (hidden) => {
      showHidden.value = hidden === undefined
    },
    { immediate: true }
  )

  watch(showHidden, (val) => {
    filter.value.hidden = val ? undefined : false
  })

  watch(
    () => filter.value.deprecated,
    (deprecated) => {
      showDeprecated.value = deprecated === undefined
    },
    { immediate: true }
  )

  watch(showDeprecated, (val) => {
    filter.value.deprecated = val ? undefined : false
  })
</script>

<style scoped>
  .filter-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }

  .filter-item {
    display: flex;
    flex-direction: column;

    h4 {
      font-weight: 400;
      margin-bottom: 0.5rem;
    }
  }

  .license-filter {
    min-width: 200px;
  }
  .width-2 {
    grid-column: span 2;
  }

  .dropdown-item {
    display: grid;
    grid-template-columns: 1fr 10px;

    font-size: 0.9rem;
  }
</style>
